<template>
  <div>
    <Head title="Expense Tickets" />
    <h1 class="mb-8 text-3xl font-bold">Expense Tickets</h1>
    <h3 class="mb-6 text-2xl font-bold">Number Of Expense Tickets: {{ expenseTickets.data.length }}</h3>
    <h3 class="mb-6 text-2xl font-bold">Total: {{ formatPrice(calcTotalExpenses()) }}</h3>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block mt-4 text-gray-700">Trashed:</label>
        <select v-model="form.trashed" class="form-select mt-1 w-full">
          <option :value="null" />
          <option value="with">With Trashed</option>
          <option value="only">Only Trashed</option>
        </select>
      </search-filter>
      <date-picker label="From" v-model="form.from" class="ml-auto pr-6" />
      <date-picker label="To" v-model="form.to" class="pr-6" />
      <filter-drop-down class="mr-4" filterName="Status">
        <label class="block text-gray-700">Status:</label>
        <select v-model="form.status" class="form-select mt-1">
          <option :value="null">All</option>
          <option v-for="st in status" :key="st.id" :value="st.name">{{ st.name }}</option>
        </select>
      </filter-drop-down>
      <filter-drop-down class="mr-4" filterName="Payment Modes">
        <label class="block text-gray-700">Payment Mode:</label>
        <select v-model="form.payment_type" class="form-select mt-1">
          <option :value="null">All</option>
          <option v-for="pt in paymentTypes" :key="pt.id" :value="pt.name">{{ pt.name }}</option>
        </select>
      </filter-drop-down>
      <download-excel class="btn-indigo cursor-pointer hover:cursor-pointer" :fields="json_fields" :data="expenseTickets.data" type="xlsx" :name="`expense-tickets-reports-${getCurrentFormattedDate()}.xls`"> Download </download-excel>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">Expense Line</th>
            <th class="pb-4 pt-6 px-6">Prepared By</th>
            <th class="pb-4 pt-6 px-6">Date Created</th>
            <th class="pb-4 pt-6 px-6">Total</th>
            <th class="pb-4 pt-6 px-6">Payment Mode</th>
            <th class="pb-4 pt-6 px-6">Status</th>
            <th class="pb-4 pt-6 px-6"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="expenseTicket in expenseTickets.data" :key="expenseTickets.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500">
                {{ expenseTicket.title }}
                <icon v-if="expenseTicket.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
                <div v-if="expenseTicket.requesting_staff">
                  {{ expenseTicket.requesting_staff.name }}
                </div>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
                {{ formatDate(expenseTicket.date_created) }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
                {{ formatPrice(expenseTicket.total) }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
                <div v-if="expenseTicket.payment_type">
                  {{ expenseTicket.payment_type.name }}
                </div>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
                {{ capitalisedFirstLetter(expenseTicket.status) }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link class="flex items-center px-4" tabindex="-1">
                <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="expenseTickets.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="6">No Expense Tickets found.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="expenseTickets.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import FilterDropDown from '@/Shared/FilterDropDown'
import DatePicker from '@/Shared/DatePicker'
import JsonExcel from 'vue-json-excel3'

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
    FilterDropDown,
    DatePicker,
    downloadExcel: JsonExcel,
  },
  layout: Layout,
  props: {
    filters: Object,
    expenseTickets: Object,
    paymentTypes: Array,
    status: Array,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        status: this.filters.status,
        trashed: this.filters.trashed,
        payment_type: this.filters.payment_type,
        from: this.filters.from,
        to: this.filters.to,
      },
      json_fields: {
        'Expense Line': 'title',
        'Prepared By': 'requesting_staff.name',
        'Date Created': {
          field: 'date_created',
          callback: (value) => {
            let date = new Date(value)
            return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()
          },
        },
        'Payment Mode': 'payment_type.name',
        'Bank Name': 'bank_account_name',
        'Account Number': 'bank_account_number',
        'Nafa Code': 'nafa_code',
        'In Favour Of': 'in_favour_of',
        'Cheque Number': 'cheque_number',
        'Wallet Number': 'wallet_number',
        Status: {
          field: 'status',
          callback: (value) => {
            return this.capitalisedFirstLetter(value)
          },
        },
        Total: 'total',
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/reports/expense-tickets', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    getCurrentFormattedDate() {
      const currentDate = new Date()

      const year = currentDate.getFullYear()
      const month = String(currentDate.getMonth() + 1).padStart(2, '0')
      const day = String(currentDate.getDate()).padStart(2, '0')
      const hours = String(currentDate.getHours()).padStart(2, '0')
      const minutes = String(currentDate.getMinutes()).padStart(2, '0')
      const seconds = String(currentDate.getSeconds()).padStart(2, '0')

      const formattedDate = `${year}_${month}_${day}_${hours}${minutes}${seconds}`

      return formattedDate
    },

    calcTotalExpenses() {
      let total = 0
      this.expenseTickets.data.forEach((expense) => {
        total += expense.total
      })
      return total
    },

    formatPrice(price) {
      const currency = this.$page.props.auth.user.account.currency
      return new Intl.NumberFormat('en-GM', { style: 'currency', currency }).format(price)
    },

    capitalisedFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },

    reset() {
      this.form = mapValues(this.form, () => null)
    },
  },
}
</script>
